import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

class KnowledgeService {
  async getDocuments(filters: any = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('name', filters.search);
      if (filters.page) {
        const limit = filters.limit || 10;
        const skip = (filters.page - 1) * limit;
        params.append('skip', skip.toString());
        params.append('limit', limit.toString());
      }
      const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.LIST, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  async createDocument(data: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DOCUMENTS.CREATE, data);
      return response.data;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  async getDocumentById(id: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DOCUMENTS.GET_BY_ID(id));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }

  async updateDocument(id: string, data: any) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.DOCUMENTS.UPDATE(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  async deleteDocument(id: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.DOCUMENTS.DELETE(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  async uploadFile(file: File) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.put(API_ENDPOINTS.STORAGE.UPLOAD, formData);
      return response.data.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async uploadFileToStore(storeId: string, file: File) {
    try {
      // 1. Upload the file
      const uploadData = await this.uploadFile(file);
      
      // 2. Get the current document store
      const store = await this.getDocumentById(storeId);
      
      // 3. Add the new document URL to the store's document_urls array
      const newDocumentUrl = {
        name: file.name,
        key: uploadData.key,
        url: uploadData.url,
        fileType: file.type,
        status: 'completed', // Assuming upload is successful
        useAsExample: false,
      };
      
      const updatedDocumentUrls = [...store.document_urls, newDocumentUrl];
      
      // 4. Update the document store with the new document URL
      return await this.updateDocument(storeId, { document_urls: updatedDocumentUrls });
    } catch (error) {
      console.error('Error uploading file to store:', error);
      throw error;
    }
  }

  async updateDocumentInStore(storeId: string, documentId: string, updates: any) {
    try {
      const store = await this.getDocumentById(storeId);
      const updatedDocumentUrls = store.document_urls.map(doc => {
        if (doc._id === documentId) {
          return { ...doc, ...updates };
        }
        return doc;
      });
      return await this.updateDocument(storeId, { document_urls: updatedDocumentUrls });
    } catch (error) {
      console.error('Error updating document in store:', error);
      throw error;
    }
  }

  async deleteDocumentFromStore(storeId: string, documentId: string) {
    try {
      const store = await this.getDocumentById(storeId);
      const updatedDocumentUrls = store.document_urls.filter(doc => doc._id !== documentId);
      return await this.updateDocument(storeId, { document_urls: updatedDocumentUrls });
    } catch (error) {
      console.error('Error deleting document from store:', error);
      throw error;
    }
  }
}

export const knowledgeService = new KnowledgeService();
